import * as actions from '@actions';
import { type Dispatch, useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { Select } from 'antd';
import { infoModal } from 'vl-common/src/components/Modals';
import { type RegistrationFormInstance, useRegistrationFormInstance } from '../useSelfRegistrationFormInstance';
import type { RegistrationFormKeys, TRegistrationForm } from '../TRegistrationForm';
import i18n from 'vl-common/src/lib/i18n';

const t = i18n.getFixedT(null, 'common');

const fetchSuggestedAddresses = async (
  searchedAddress: string,
  dispatch: Dispatch
): Promise<
  {
    suggestion: string;
    id: string;
  }[]
> => {
  const res = await dispatch(actions.getSuggestedAddresses(searchedAddress), false);
  return res?.result?.hits?.map((address) => {
    return { suggestion: address.suggestion, id: address.id };
  });
};

const onError = (e: unknown, form: RegistrationFormInstance, searchFieldName: RegistrationFormKeys) => {
  if (e instanceof Error) {
    form.setFields([{ name: searchFieldName, errors: [e.message] }]);
  }
};

// Missing function definitions

const fetchPractices = async (
  postcode: string,
  dispatch: Dispatch
): Promise<
  {
    address: Pick<TRegistrationForm, 'gp_email' | 'gp_ods_code' | 'gp_practice_name' | 'gp_postcode'>;
    label: string;
    value: string;
  }[]
> => {
  const res = await dispatch(actions.fetchGPs(postcode), false);
  return (
    res?.practices?.map((practice: any) => ({
      address: {
        gp_email: practice.email,
        gp_ods_code: practice.gp_ods_code,
        gp_practice_name: practice.practice_name,
        gp_postcode: practice.postcode
      },
      label: practice.practice_name,
      value: practice.gp_ods_code
    })) || []
  );
};

function onSuccess(
  data: Awaited<ReturnType<typeof fetchPractices>>,
  postcode: string,
  form: RegistrationFormInstance,
  type: 'gp_practice',
  callback?: () => void
): void;
function onSuccess(
  data: Awaited<ReturnType<typeof fetchPractices>>,
  postcode: string,
  form: RegistrationFormInstance,
  type: 'gp_practice',
  callback?: () => void
) {
  form.setFields([{ name: 'postcode', errors: [] }]);

  const m = infoModal({
    title: `Select ${type === 'gp_practice' ? 'GP practice' : 'address'} for ${postcode.toUpperCase()}`,
    width: 600,
    icon: null,
    bodyStyle: { padding: '24px' }
  });

  m.update({
    content: (
      <Select
        placeholder={t('Select address')}
        onChange={(_, option) => {
          const { address } = Array.isArray(option)
            ? option[0]
            : (option as {
                address: Pick<TRegistrationForm, 'gp_email' | 'gp_ods_code' | 'gp_practice_name' | 'gp_postcode'>;
              });

          form.setFieldsValue({ ...address });

          callback?.();

          m.destroy();
        }}
        // Looks pointless, but is necessary to satisfy an unintended strictness in
        // the generic bit of the Select wrapper
        options={data?.map(({ address, label, value }) => ({
          value,
          address,
          label
        }))}
        data-testid="address-select"
      />
    )
  });
}

export const useSearchPractices = () => {
  const dispatch = useDispatch();
  const form = useRegistrationFormInstance();

  return useMutation({
    mutationFn: (postcode: string) => fetchPractices(postcode, dispatch),
    onError: (e) => onError(e, form, 'gp_postcode'),
    onSuccess: (data, postcode) => onSuccess(data, postcode, form, 'gp_practice')
  });
};
export const useAddressesAutoComplete = () => {
  const dispatch = useDispatch();
  const form = useRegistrationFormInstance();

  return {
    ...useMutation({
      mutationFn: (searchedAddress: string) => fetchSuggestedAddresses(searchedAddress, dispatch),
      onError: (e) => onError(e, form, 'postcode')
    })
  };
};
